/**
 * Legal Module - Module 2: NDA, IP Assignment, and Terms of Service
 *
 * Guides users through required legal agreements based on their role.
 * Tracks section acknowledgment and captures digital signatures.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { FileText, Check, ChevronRight, AlertTriangle } from 'lucide-react';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { useOnboardingTelemetry } from '../../hooks/useOnboardingTelemetry';
import type { OnboardingRole, PolicyType, SignatureData } from '../../types/onboarding';
import { getPoliciesForRole, getPolicyById } from '../../lib/onboarding/policies';
import { LEGAL_REQUIREMENTS } from '../../lib/onboarding/steps';
import PolicyViewer from './PolicyViewer';
import DigitalSignature from './DigitalSignature';
import { StepSection, StepCard } from './OnboardingStep';
import { cn } from '../../lib/utils';

interface LegalModuleProps {
  onComplete: () => void;
  role: OnboardingRole;
}

type LegalStep = 'overview' | PolicyType | 'complete';

export default function LegalModule({ onComplete, role }: LegalModuleProps) {
  const [currentStep, setCurrentStep] = useState<LegalStep>('overview');
  const [acknowledgedSections, setAcknowledgedSections] = useState<
    Record<PolicyType, string[]>
  >({
    nda: [],
    ip_assignment: [],
    tos: [],
  });

  const {
    ndaSignature,
    ipAssignmentSignature,
    tosAcceptedAt,
    signPolicy,
  } = useOnboardingStore();

  const { trackEvent } = useOnboardingTelemetry();

  // Get required policies for this role
  const requiredPolicies = useMemo(() => {
    const requirements = LEGAL_REQUIREMENTS[role];
    const policies: PolicyType[] = [];

    if (requirements.nda) policies.push('nda');
    if (requirements.ip_assignment) policies.push('ip_assignment');
    if (requirements.tos) policies.push('tos');

    return policies;
  }, [role]);

  // Check completion status for each policy
  const policyStatus = useMemo(() => {
    return {
      nda: !!ndaSignature,
      ip_assignment: !!ipAssignmentSignature,
      tos: !!tosAcceptedAt,
    };
  }, [ndaSignature, ipAssignmentSignature, tosAcceptedAt]);

  // Check if all required policies are complete
  const allPoliciesComplete = useMemo(() => {
    return requiredPolicies.every((policy) => policyStatus[policy]);
  }, [requiredPolicies, policyStatus]);

  // Handle section acknowledgment
  const handleSectionAcknowledge = useCallback(
    (policyType: PolicyType, sectionId: string) => {
      setAcknowledgedSections((prev) => ({
        ...prev,
        [policyType]: [...prev[policyType], sectionId],
      }));
    },
    []
  );

  // Handle policy signature
  const handleSign = useCallback(
    (policyType: PolicyType, signature: SignatureData) => {
      signPolicy(policyType, signature);
      trackEvent('policy_signed', { policyType });

      // Move to next policy or complete
      const currentIndex = requiredPolicies.indexOf(policyType);
      if (currentIndex < requiredPolicies.length - 1) {
        setCurrentStep(requiredPolicies[currentIndex + 1]);
      } else {
        setCurrentStep('complete');
      }
    },
    [signPolicy, trackEvent, requiredPolicies]
  );

  // Auto-advance when all complete
  useEffect(() => {
    if (allPoliciesComplete && currentStep === 'complete') {
      // Small delay for user to see completion
      const timer = setTimeout(() => {
        onComplete();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [allPoliciesComplete, currentStep, onComplete]);

  // Render based on current step
  if (currentStep === 'overview') {
    return (
      <LegalOverview
        role={role}
        requiredPolicies={requiredPolicies}
        policyStatus={policyStatus}
        onStartPolicy={(policy) => setCurrentStep(policy)}
      />
    );
  }

  if (currentStep === 'complete') {
    return (
      <LegalComplete
        requiredPolicies={requiredPolicies}
        onContinue={onComplete}
      />
    );
  }

  // Render policy viewer + signature
  const policy = getPolicyById(currentStep);
  const isLastPolicy =
    requiredPolicies.indexOf(currentStep) === requiredPolicies.length - 1;

  return (
    <div className="space-y-6">
      {/* Policy Progress */}
      <div className="flex items-center gap-2 mb-6">
        {requiredPolicies.map((policyType, index) => (
          <div key={policyType} className="flex items-center gap-2">
            <button
              onClick={() =>
                policyStatus[policyType] || policyType === currentStep
                  ? setCurrentStep(policyType)
                  : null
              }
              disabled={
                !policyStatus[policyType] && policyType !== currentStep
              }
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              style={{
                backgroundColor:
                  policyType === currentStep
                    ? 'var(--color-accent)'
                    : policyStatus[policyType]
                    ? 'var(--color-success)'
                    : 'var(--color-bg-tertiary)',
                color:
                  policyType === currentStep || policyStatus[policyType]
                    ? 'white'
                    : 'var(--color-text-muted)',
              }}
            >
              {policyStatus[policyType] ? (
                <Check className="w-3 h-3" />
              ) : (
                <span>{index + 1}</span>
              )}
              <span>{getPolicyById(policyType).title}</span>
            </button>
            {index < requiredPolicies.length - 1 && (
              <ChevronRight
                className="w-4 h-4"
                style={{ color: 'var(--color-border)' }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Policy Viewer */}
      <PolicyViewer
        policy={policy}
        acknowledgedSections={acknowledgedSections[currentStep]}
        onSectionAcknowledge={(sectionId) =>
          handleSectionAcknowledge(currentStep, sectionId)
        }
        onAllSectionsAcknowledged={() => {
          // Enable signature
        }}
      />

      {/* Digital Signature */}
      <DigitalSignature
        documentTitle={policy.title}
        documentVersion={policy.version}
        existingSignature={
          currentStep === 'nda'
            ? ndaSignature
            : currentStep === 'ip_assignment'
            ? ipAssignmentSignature
            : null
        }
        isComplete={policyStatus[currentStep]}
        onSign={(signature) => handleSign(currentStep, signature)}
      />

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4">
        <button
          onClick={() => {
            const currentIndex = requiredPolicies.indexOf(currentStep);
            if (currentIndex > 0) {
              setCurrentStep(requiredPolicies[currentIndex - 1]);
            } else {
              setCurrentStep('overview');
            }
          }}
          className="px-4 py-2 rounded-lg text-sm transition-colors"
          style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            color: 'var(--color-text-secondary)',
          }}
        >
          Back
        </button>

        {policyStatus[currentStep] && (
          <button
            onClick={() => {
              const currentIndex = requiredPolicies.indexOf(currentStep);
              if (currentIndex < requiredPolicies.length - 1) {
                setCurrentStep(requiredPolicies[currentIndex + 1]);
              } else {
                setCurrentStep('complete');
              }
            }}
            className="px-6 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-bg)',
            }}
          >
            {isLastPolicy ? 'Complete Legal Agreements' : 'Next Document'}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Legal Overview Component
// ============================================================================

interface LegalOverviewProps {
  role: OnboardingRole;
  requiredPolicies: PolicyType[];
  policyStatus: Record<PolicyType, boolean>;
  onStartPolicy: (policy: PolicyType) => void;
}

function LegalOverview({
  role,
  requiredPolicies,
  policyStatus,
  onStartPolicy,
}: LegalOverviewProps) {
  const completedCount = requiredPolicies.filter((p) => policyStatus[p]).length;
  const firstIncomplete = requiredPolicies.find((p) => !policyStatus[p]);

  return (
    <div className="space-y-6">
      <StepSection
        title="Legal Agreements Overview"
        description="Please review and sign the following documents to proceed. Take your time to read each section carefully."
      >
        <StepCard>
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle
              className="w-5 h-5"
              style={{ color: 'var(--color-warning)' }}
            />
            <span
              className="text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              These are legally binding agreements. If you have questions, please
              contact legal@lukhas.ai before signing.
            </span>
          </div>

          {/* Progress */}
          <div
            className="p-3 rounded-lg mb-4"
            style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className="text-sm font-medium"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {completedCount} of {requiredPolicies.length} documents signed
              </span>
              <span
                className="text-sm"
                style={{ color: 'var(--color-accent)' }}
              >
                {Math.round((completedCount / requiredPolicies.length) * 100)}%
              </span>
            </div>
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ backgroundColor: 'var(--color-bg-secondary)' }}
            >
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  backgroundColor: 'var(--color-accent)',
                  width: `${(completedCount / requiredPolicies.length) * 100}%`,
                }}
              />
            </div>
          </div>

          {/* Document List */}
          <div className="space-y-3">
            {requiredPolicies.map((policyType) => {
              const policy = getPolicyById(policyType);
              const isComplete = policyStatus[policyType];

              return (
                <button
                  key={policyType}
                  onClick={() => onStartPolicy(policyType)}
                  className={cn(
                    'w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all',
                    'hover:scale-[1.01]'
                  )}
                  style={{
                    backgroundColor: isComplete
                      ? 'rgba(34, 197, 94, 0.1)'
                      : 'var(--color-bg-tertiary)',
                    border: isComplete
                      ? '1px solid var(--color-success)'
                      : '1px solid var(--color-border)',
                  }}
                >
                  <div
                    className="p-2 rounded-lg"
                    style={{
                      backgroundColor: isComplete
                        ? 'var(--color-success)'
                        : 'var(--color-bg-secondary)',
                    }}
                  >
                    {isComplete ? (
                      <Check className="w-5 h-5" style={{ color: 'white' }} />
                    ) : (
                      <FileText
                        className="w-5 h-5"
                        style={{ color: 'var(--color-text-secondary)' }}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className="font-medium"
                      style={{
                        color: isComplete
                          ? 'var(--color-success)'
                          : 'var(--color-text-primary)',
                      }}
                    >
                      {policy.title}
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      v{policy.version} | {policy.sections.length} sections
                    </p>
                  </div>
                  <ChevronRight
                    className="w-5 h-5"
                    style={{ color: 'var(--color-text-muted)' }}
                  />
                </button>
              );
            })}
          </div>
        </StepCard>
      </StepSection>

      {/* Start Button */}
      {firstIncomplete && (
        <button
          onClick={() => onStartPolicy(firstIncomplete)}
          className="w-full py-4 rounded-xl font-semibold transition-all hover:scale-[1.01]"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'var(--color-bg)',
          }}
        >
          {completedCount > 0 ? 'Continue Signing' : 'Begin Review'}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Legal Complete Component
// ============================================================================

interface LegalCompleteProps {
  requiredPolicies: PolicyType[];
  onContinue: () => void;
}

function LegalComplete({ requiredPolicies, onContinue }: LegalCompleteProps) {
  return (
    <div className="text-center py-12">
      <div
        className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--color-success)' }}
      >
        <Check className="w-10 h-10" style={{ color: 'white' }} />
      </div>

      <h3
        className="text-2xl font-bold mb-2"
        style={{ color: 'var(--color-text-primary)' }}
      >
        All Documents Signed
      </h3>

      <p
        className="text-base mb-6 max-w-md mx-auto"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        Thank you for completing the legal agreements. Copies will be available in
        your dashboard under the Legal section.
      </p>

      <div
        className="inline-flex flex-wrap items-center justify-center gap-2 mb-8"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {requiredPolicies.map((policyType, index) => (
          <span key={policyType} className="flex items-center gap-1 text-sm">
            <Check className="w-4 h-4" style={{ color: 'var(--color-success)' }} />
            {getPolicyById(policyType).title}
            {index < requiredPolicies.length - 1 && (
              <span className="mx-2" style={{ color: 'var(--color-border)' }}>
                |
              </span>
            )}
          </span>
        ))}
      </div>

      <button
        onClick={onContinue}
        className="px-8 py-3 rounded-xl font-semibold transition-all hover:scale-105"
        style={{
          backgroundColor: 'var(--color-accent)',
          color: 'var(--color-bg)',
        }}
      >
        Continue to Security Training
      </button>
    </div>
  );
}
